import React, { useEffect, useState } from 'react';
import ConstructionLayout from './ConstructionLayout';
import TablePagination from './TablePagination';
import { useModule } from '../../contexts/ModuleContext';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchSiteData } from '../../services/siteDataService';
import { Filter, Download, ArrowRight, MoreVertical, RefreshCw } from 'lucide-react';
import { Feature, isFeatureEnabled } from '../../config/features';

const rowNetVolume = (row) =>
  row.volume !== null && row.volume !== undefined
    ? Number(row.volume) || 0
    : (Number(row.cut) || 0) - (Number(row.fill) || 0);

export default function StockpilesPage() {
  const { isDarkMode } = useTheme();
  const { activeProject } = useModule();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let isMounted = true;
    if (!activeProject?.numericId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setCurrentPage(1);
    fetchSiteData(activeProject.numericId, 'Material')
      .then((res) => {
        if (isMounted) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching material site data:', err);
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [activeProject?.numericId]);

  // Dynamic totals calculation across all items
  const totalArea = data.reduce((acc, row) => acc + (Number(row.area) || 0), 0);
  const totalCut = data.reduce((acc, row) => acc + (Number(row.cut) || 0), 0);
  const totalFill = data.reduce((acc, row) => acc + (Number(row.fill) || 0), 0);
  const totalNetVolume = data.reduce((acc, row) => acc + rowNetVolume(row), 0);
  const rowMenu = isFeatureEnabled('STOCKPILES_ROW_MENU');

  // Slice paginated items
  const paginatedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <ConstructionLayout title="Stockpiles" subtitle="Inventory from drone surveys · all surveys of this project">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Action Toolbar */}
        {(isFeatureEnabled('STOCKPILES_FILTERS') || isFeatureEnabled('STOCKPILES_EXPORT')) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Feature name="STOCKPILES_FILTERS">
          <button style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#D1D5DB'}`, color: isDarkMode ? '#F3F4F6' : '#111827', fontSize: '13px', cursor: 'pointer' }}>
            <Filter size={14} /> Filters
          </button>
          </Feature>
          <Feature name="STOCKPILES_EXPORT">
          <button style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#D1D5DB'}`, color: isDarkMode ? '#F3F4F6' : '#111827', fontSize: '13px', cursor: 'pointer' }}>
            <Download size={14} /> Export
          </button>
          </Feature>
        </div>
        )}

        {/* Main Grid: Data Table + Summary Sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
          {/* Left Table Container */}
          <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <RefreshCw className="animate-spin" size={18} /> Loading stockpiles...
              </div>
            ) : data.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                No stockpile records found for this project.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      <th style={{ padding: '12px 14px' }}>ID</th>
                      <th style={{ padding: '12px 14px' }}>Name / Material</th>
                      <th style={{ padding: '12px 14px' }}>Type</th>
                      <th style={{ padding: '12px 14px' }}>Area (m²)</th>
                      <th style={{ padding: '12px 14px' }}>Net Vol (m³)</th>
                      <th style={{ padding: '12px 14px' }}>Cut (m³)</th>
                      <th style={{ padding: '12px 14px' }}>Fill (m³)</th>
                      <th style={{ padding: '12px 14px' }}>Surface</th>
                      <th style={{ padding: '12px 14px' }}>Unit</th>
                      {rowMenu && <th style={{ padding: '12px 14px' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((row) => {
                      const netVol = rowNetVolume(row);
                      const isPositive = netVol >= 0;
                      return (
                        <tr key={row.id} style={{ borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#F3F4F6'}`, color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>
                          <td style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: row.color === 'orange' ? '#F59E0B' : row.color === 'green' ? '#10B981' : '#3B82F6' }} />
                            {row.id}
                          </td>
                          <td style={{ padding: '12px 14px', fontWeight: 600 }}>{row.name}</td>
                          <td style={{ padding: '12px 14px' }}>{row.type}</td>
                          <td style={{ padding: '12px 14px' }}>{row.area ? Number(row.area).toFixed(2) : '—'}</td>
                          <td style={{ padding: '12px 14px', color: isPositive ? '#10B981' : '#EF4444', fontWeight: 600 }}>
                            {isPositive ? `+${netVol.toFixed(2)}` : netVol.toFixed(2)}
                          </td>
                          <td style={{ padding: '12px 14px' }}>{row.cut !== null ? Number(row.cut).toFixed(2) : '0.00'}</td>
                          <td style={{ padding: '12px 14px', color: '#EF4444' }}>{row.fill !== null ? Number(row.fill).toFixed(2) : '0.00'}</td>
                          <td style={{ padding: '12px 14px' }}>{row.surface || '—'}</td>
                          <td style={{ padding: '12px 14px' }}>{row.unit_system || 'metric'}</td>
                          {rowMenu && (
                            <td style={{ padding: '12px 14px' }}>
                              <button style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer' }}>
                                <MoreVertical size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {/* TOTAL ROW */}
                    <tr style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                      <td colSpan={3} style={{ padding: '14px' }}>TOTAL ({data.length} items)</td>
                      <td style={{ padding: '14px' }}>{totalArea.toFixed(2)} m²</td>
                      <td style={{ padding: '14px', color: totalNetVolume >= 0 ? '#10B981' : '#EF4444' }}>
                        {totalNetVolume >= 0 ? `+${totalNetVolume.toFixed(2)}` : totalNetVolume.toFixed(2)} m³
                      </td>
                      <td style={{ padding: '14px' }}>{totalCut.toFixed(2)} m³</td>
                      <td style={{ padding: '14px', color: '#EF4444' }}>{totalFill.toFixed(2)} m³</td>
                      <td colSpan={rowMenu ? 3 : 2} style={{ padding: '14px' }}>—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Reusable Pagination Strip */}
            {!loading && (
              <TablePagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={data.length}
              />
            )}
          </div>

          {/* Right Summary Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Stockpile Summary Card */}
            <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>Stockpile Summary</div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', padding: '10px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Total Stockpiles</span>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>{data.length}</div>
                </div>
                <div style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', padding: '10px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Total Area</span>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>{totalArea.toFixed(2)} m²</div>
                </div>
                <div style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', padding: '10px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Total Cut</span>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>{totalCut.toFixed(2)} m³</div>
                </div>
                <div style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', padding: '10px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Total Fill</span>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#EF4444' }}>{totalFill.toFixed(2)} m³</div>
                </div>
              </div>

              <div style={{ backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB', padding: '10px', borderRadius: '8px' }}>
                <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Net Volume</span>
                <div style={{ fontSize: '16px', fontWeight: 800, color: totalNetVolume >= 0 ? '#10B981' : '#EF4444' }}>
                  {totalNetVolume >= 0 ? `+${totalNetVolume.toFixed(2)}` : totalNetVolume.toFixed(2)} m³
                </div>
              </div>
            </div>

            {/* Staging Data Card */}
            <Feature name="STOCKPILES_STAGING_INFO">
            <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                Staging Table Info
              </div>
              
              <div style={{ fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div><strong>Table:</strong> site_data_staging</div>
                <div><strong>Project ID:</strong> {activeProject?.id || '—'}</div>
                <div><strong>Category:</strong> Material</div>
                <div><strong>Material Count:</strong> {data.length}</div>
              </div>

              <Feature name="STOCKPILES_REPORT">
              <button
                style={{
                  marginTop: '6px',
                  backgroundColor: isDarkMode ? '#161B26' : '#EFF6FF',
                  color: '#0082F8',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                }}
              >
                View Stockpile Report
                <ArrowRight size={14} />
              </button>
              </Feature>
            </div>
            </Feature>
          </div>
        </div>
      </div>
    </ConstructionLayout>
  );
}
